import { Injectable } from '@angular/core';
import { AssetRepositoryPort } from '../../application/ports';
import { Asset, AssetType } from '../../domain/models';

/**
 * Mock asset repository for development and testing
 * Provides sample data without requiring backend API
 */
@Injectable()
export class MockAssetRepositoryAdapter implements AssetRepositoryPort {
  private readonly mockAssets: Asset[] = this.generateMockData();

  /**
   * Load all mock assets
   */
  async loadAll(): Promise<Asset[]> {
    // Simulate network delay
    await this.delay(500);
    return this.mockAssets;
  }

  /**
   * Load children of a specific asset
   */
  async loadChildren(parentId: string): Promise<Asset[]> {
    await this.delay(300);
    return this.mockAssets.filter((asset) => asset.parentId === parentId);
  }

  /**
   * Load a single asset by ID
   */
  async loadById(id: string): Promise<Asset | null> {
    await this.delay(200);
    return this.mockAssets.find((asset) => asset.id === id) || null;
  }

  /**
   * Search assets by query
   */
  async search(query: string): Promise<Asset[]> {
    await this.delay(300);
    const lowerQuery = query.toLowerCase();
    return this.mockAssets.filter((asset) =>
      asset.name.toLowerCase().includes(lowerQuery),
    );
  }

  /**
   * Generate mock asset hierarchy with realistic refinery assets
   */
  private generateMockData(): Asset[] {
    const assets: Asset[] = [];

    // ========== TotalEnergies Refinery Site ==========
    const refinery = new Asset(
      'refinery1',
      'TotalEnergies Normandy Refinery',
      AssetType.REFINERY,
      '',
      null,
      [],
      {
        description:
          'Integrated refinery and petrochemicals site in Normandy, France, with multi-product processing trains',
        owner: 'Operations Management',
        tags: ['biorefinery', 'france', 'critical'],
        properties: {
          location: 'Normandy, France',
          capacity: '250000 tons/year',
          feedstock: 'Crude oil, bio-feedstocks',
        },
      },
    );
    assets.push(refinery);

    // ========== Crude Distillation Unit (CDU) ==========
    const cduUnit = new Asset(
      'unit1',
      'Crude Distillation Unit (CDU-100)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Primary crude oil separation unit',
        owner: 'Process Engineering',
        tags: ['distillation', 'primary', 'critical'],
        properties: { capacity: '80000 bbl/day', temperature: '350-400°C' },
      },
    );
    assets.push(cduUnit);

    // CDU Storage Tanks
    const crudeTank1 = new Asset(
      'tank1',
      'Crude Storage Tank T-101',
      AssetType.TANK,
      '',
      'unit1',
      [],
      {
        description: 'Crude oil storage tank',
        properties: {
          capacity: '100000 bbl',
          current_level: '85000 bbl',
          product: 'Crude Oil',
        },
      },
    );
    assets.push(crudeTank1);

    // CDU Tank Flowmeter
    const flowmeter1 = new Asset(
      'flow1',
      'Inlet Flowmeter FM-101',
      AssetType.FLOWMETER,
      '',
      'tank1',
      [],
      {
        description: 'Measures crude oil flow into tank',
        properties: {
          range: '0-5000 bbl/hr',
          accuracy: '±0.5%',
          current_flow: '3200 bbl/hr',
        },
      },
    );
    assets.push(flowmeter1);

    // CDU Pipelines
    const pipeline1 = new Asset(
      'pipe1',
      'Crude Feed Pipeline P-101',
      AssetType.PIPELINE,
      '',
      'unit1',
      [],
      {
        description: 'Main crude oil feed pipeline to distillation tower',
        properties: {
          diameter: '24 inch',
          length: '500m',
          material: 'Carbon Steel',
        },
      },
    );
    assets.push(pipeline1);

    const flowmeter2 = new Asset(
      'flow2',
      'Feed Flowmeter FM-102',
      AssetType.FLOWMETER,
      '',
      'pipe1',
      [],
      {
        description: 'Measures crude feed rate to CDU',
        properties: {
          range: '0-8000 bbl/hr',
          accuracy: '±0.3%',
          current_flow: '6500 bbl/hr',
        },
      },
    );
    assets.push(flowmeter2);

    // ========== Hydrocracking Unit (HCU) ==========
    const hcuUnit = new Asset(
      'unit2',
      'Hydrocracking Unit (HCU-200)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Converts heavy oil fractions into lighter products',
        owner: 'Process Engineering',
        tags: ['hydrocracking', 'conversion', 'high-pressure'],
        properties: {
          capacity: '40000 bbl/day',
          pressure: '150 bar',
          temperature: '420°C',
        },
      },
    );
    assets.push(hcuUnit);

    // HCU Storage Tanks
    const gasoilTank = new Asset(
      'tank2',
      'Gas Oil Feed Tank T-201',
      AssetType.TANK,
      '',
      'unit2',
      [],
      {
        description: 'Heavy gas oil feed storage',
        properties: {
          capacity: '50000 bbl',
          current_level: '38000 bbl',
          product: 'Heavy Gas Oil',
        },
      },
    );
    assets.push(gasoilTank);

    const flowmeter3 = new Asset(
      'flow3',
      'Tank Outlet Flowmeter FM-201',
      AssetType.FLOWMETER,
      '',
      'tank2',
      [],
      {
        description: 'Monitors gas oil flow from tank',
        properties: {
          range: '0-3000 bbl/hr',
          accuracy: '±0.4%',
          current_flow: '2100 bbl/hr',
        },
      },
    );
    assets.push(flowmeter3);

    const hydrogenPipe = new Asset(
      'pipe2',
      'Hydrogen Feed Pipeline P-202',
      AssetType.PIPELINE,
      '',
      'unit2',
      [],
      {
        description: 'High-pressure hydrogen supply line',
        properties: {
          diameter: '12 inch',
          length: '300m',
          material: 'Stainless Steel',
          pressure_rating: '200 bar',
        },
      },
    );
    assets.push(hydrogenPipe);

    const flowmeter4 = new Asset(
      'flow4',
      'Hydrogen Flowmeter FM-203',
      AssetType.FLOWMETER,
      '',
      'pipe2',
      [],
      {
        description: 'Measures hydrogen feed rate',
        properties: {
          range: '0-50000 Nm³/hr',
          accuracy: '±0.5%',
          current_flow: '38000 Nm³/hr',
        },
      },
    );
    assets.push(flowmeter4);

    // ========== Reforming Unit ==========
    const reformingUnit = new Asset(
      'unit3',
      'Catalytic Reforming Unit (CRU-300)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Produces high-octane gasoline components',
        owner: 'Process Engineering',
        tags: ['reforming', 'gasoline', 'catalyst'],
        properties: {
          capacity: '25000 bbl/day',
          temperature: '500-540°C',
          pressure: '15 bar',
        },
      },
    );
    assets.push(reformingUnit);

    const naphthaTank = new Asset(
      'tank3',
      'Naphtha Feed Tank T-301',
      AssetType.TANK,
      '',
      'unit3',
      [],
      {
        description: 'Naphtha feedstock storage',
        properties: {
          capacity: '30000 bbl',
          current_level: '22000 bbl',
          product: 'Naphtha',
        },
      },
    );
    assets.push(naphthaTank);

    const flowmeter5 = new Asset(
      'flow5',
      'Naphtha Feed Flowmeter FM-301',
      AssetType.FLOWMETER,
      '',
      'tank3',
      [],
      {
        description: 'Measures naphtha feed rate',
        properties: {
          range: '0-2000 bbl/hr',
          accuracy: '±0.3%',
          current_flow: '1400 bbl/hr',
        },
      },
    );
    assets.push(flowmeter5);

    const reformateTank = new Asset(
      'tank4',
      'Reformate Product Tank T-302',
      AssetType.TANK,
      '',
      'unit3',
      [],
      {
        description: 'High-octane reformate product storage',
        properties: {
          capacity: '40000 bbl',
          current_level: '18500 bbl',
          product: 'Reformate',
          octane: '95 RON',
        },
      },
    );
    assets.push(reformateTank);

    // ========== Product Blending Area ==========
    const blendingUnit = new Asset(
      'unit4',
      'Product Blending Unit (BLD-400)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Blends final gasoline and diesel products',
        owner: 'Operations',
        tags: ['blending', 'products', 'quality'],
        properties: { capacity: '60000 bbl/day' },
      },
    );
    assets.push(blendingUnit);

    const gasolineTank = new Asset(
      'tank5',
      'Premium Gasoline Tank T-401',
      AssetType.TANK,
      '',
      'unit4',
      [],
      {
        description: 'Premium gasoline finished product storage',
        properties: {
          capacity: '80000 bbl',
          current_level: '65000 bbl',
          product: 'Premium Gasoline',
          grade: 'SP95-E10',
        },
      },
    );
    assets.push(gasolineTank);

    const dieselTank = new Asset(
      'tank6',
      'Diesel Storage Tank T-402',
      AssetType.TANK,
      '',
      'unit4',
      [],
      {
        description: 'Diesel finished product storage',
        properties: {
          capacity: '100000 bbl',
          current_level: '72000 bbl',
          product: 'Diesel B7',
        },
      },
    );
    assets.push(dieselTank);

    const blendPipe = new Asset(
      'pipe3',
      'Blend Transfer Pipeline P-401',
      AssetType.PIPELINE,
      '',
      'unit4',
      [],
      {
        description: 'Transfer line to loading terminal',
        properties: {
          diameter: '20 inch',
          length: '1200m',
          material: 'Carbon Steel',
        },
      },
    );
    assets.push(blendPipe);

    const flowmeter6 = new Asset(
      'flow6',
      'Loading Flowmeter FM-401',
      AssetType.FLOWMETER,
      '',
      'pipe3',
      [],
      {
        description: 'Measures product flow to loading terminal',
        properties: {
          range: '0-6000 bbl/hr',
          accuracy: '±0.2%',
          current_flow: '4200 bbl/hr',
        },
      },
    );
    assets.push(flowmeter6);

    // ========== Utilities ==========
    const utilities = new Asset(
      'unit5',
      'Utilities & Services',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Steam, power, cooling water, and compressed air systems',
        owner: 'Utilities Team',
        tags: ['utilities', 'support'],
        properties: {},
      },
    );
    assets.push(utilities);

    const coolingTower = new Asset(
      'equip1',
      'Cooling Water System',
      AssetType.EQUIPMENT,
      '',
      'unit5',
      [],
      {
        description: 'Main cooling water circulation system',
        properties: { capacity: '50000 m³/hr', temperature: '25-35°C' },
      },
    );
    assets.push(coolingTower);

    const flowmeter7 = new Asset(
      'flow7',
      'Cooling Water Flowmeter FM-501',
      AssetType.FLOWMETER,
      '',
      'equip1',
      [],
      {
        description: 'Monitors cooling water circulation',
        properties: {
          range: '0-60000 m³/hr',
          accuracy: '±1%',
          current_flow: '42000 m³/hr',
        },
      },
    );
    assets.push(flowmeter7);

    // Utilities instrumentation
    const utilitiesPlc = new Asset(
      'ctrl1',
      'Utilities PLC Panel PLC-501',
      AssetType.CONTROLLER,
      '',
      'unit5',
      [],
      {
        description: 'Centralized control cabinet for utilities loops',
        properties: {
          vendor: 'Schneider Electric',
          protocol: 'Modbus TCP',
          redundancy: 'Hot standby',
        },
      },
    );
    assets.push(utilitiesPlc);

    const coolingTempSensor = new Asset(
      'sensor1',
      'Cooling Water Return Temperature TT-511',
      AssetType.SENSOR,
      '',
      'equip1',
      [],
      {
        description: 'Temperature sensor on cooling water return header',
        properties: {
          range: '0-80C',
          current_value: '31.6C',
          unit: 'degC',
        },
      },
    );
    assets.push(coolingTempSensor);

    const dailyReport = new Asset(
      'report1',
      'Daily Production Report',
      AssetType.REPORT,
      '',
      'refinery1',
      [],
      {
        description: 'Daily production and blending quality summary report',
        owner: 'Performance Team',
        properties: {
          format: 'pdf',
          frequency: 'daily',
        },
      },
    );
    assets.push(dailyReport);

    const startupProcedure = new Asset(
      'doc1',
      'CDU Startup and Shutdown Procedure',
      AssetType.DOCUMENT,
      '',
      'unit1',
      [],
      {
        description: 'Operational guideline for CDU transient operations',
        tags: ['hse', 'operations'],
        properties: {
          revision: 'Rev 12',
          approval_status: 'approved',
        },
      },
    );
    assets.push(startupProcedure);

    // ========== Normandy Advanced Conversion & Export ==========
    const hydrotreaterUnit = new Asset(
      'unit6',
      'Normandy Diesel Hydrotreater Unit (DHDT-610)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Ultra-low sulfur diesel treatment train',
        tags: ['diesel', 'desulfurization', 'hydrogen'],
        properties: {
          capacity: '65000 bbl/day',
          sulfur_target: '<10 ppm',
          pressure: '68 bar',
        },
      },
    );
    assets.push(hydrotreaterUnit);

    const ulsdTank = new Asset(
      'tank7',
      'ULSD Product Tank T-611',
      AssetType.TANK,
      '',
      'unit6',
      [],
      {
        description: 'Ultra-low sulfur diesel storage before dispatch',
        properties: {
          capacity: '120000 bbl',
          current_level: '91000 bbl',
          product: 'ULSD EN590',
        },
      },
    );
    assets.push(ulsdTank);

    const ulsdMeter = new Asset(
      'flow8',
      'ULSD Transfer Meter FM-612',
      AssetType.FLOWMETER,
      '',
      'tank7',
      [],
      {
        description: 'Custody transfer metering skid for truck/jetty loading',
        properties: {
          range: '0-5500 bbl/hr',
          accuracy: '±0.15%',
          current_flow: '3550 bbl/hr',
        },
      },
    );
    assets.push(ulsdMeter);

    const sulfurRecoveryUnit = new Asset(
      'unit7',
      'Normandy Sulfur Recovery Unit (SRU-720)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Claus process train for sulfur removal and recovery',
        owner: 'Environmental Operations',
        tags: ['sru', 'environment', 'h2s'],
        properties: {
          capacity: '110 t/day sulfur',
          conversion_efficiency: '99.5%',
        },
      },
    );
    assets.push(sulfurRecoveryUnit);

    const h2sSensor = new Asset(
      'sensor2',
      'Tail Gas H2S Analyzer AN-721',
      AssetType.SENSOR,
      '',
      'unit7',
      [],
      {
        description: 'Continuous analyzer for sulfur emissions compliance',
        properties: {
          range: '0-250 ppm',
          current_value: '38 ppm',
          alarm_high: '120 ppm',
        },
      },
    );
    assets.push(h2sSensor);

    const exportPipeline = new Asset(
      'pipe4',
      'Normandy Marine Export Pipeline P-730',
      AssetType.PIPELINE,
      '',
      'refinery1',
      [],
      {
        description: 'Multi-product export line to marine terminal',
        properties: {
          diameter: '28 inch',
          length: '3400m',
          service: 'diesel/gasoline/jet',
        },
      },
    );
    assets.push(exportPipeline);

    const terminalMeter = new Asset(
      'flow9',
      'Marine Terminal Metering Skid FM-731',
      AssetType.FLOWMETER,
      '',
      'pipe4',
      [],
      {
        description: 'Fiscal metering for jetty cargo transfer',
        properties: {
          range: '0-9000 bbl/hr',
          accuracy: '±0.1%',
          current_flow: '6100 bbl/hr',
        },
      },
    );
    assets.push(terminalMeter);

    // ========== Normandy Gas Processing & Conditioning ==========
    const sweeteningUnit = new Asset(
      'unit8',
      'Normandy Amine Sweetening Unit (ASU-810)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description: 'Removes CO2 and H2S from incoming natural gas',
        tags: ['gas-treatment', 'amine'],
        properties: {
          solvent: 'MDEA',
          treating_capacity: '18 mmscmd',
        },
      },
    );
    assets.push(sweeteningUnit);

    const dehydrationUnit = new Asset(
      'unit9',
      'Normandy Molecular Sieve Dehydration Unit (MDU-820)',
      AssetType.UNIT,
      '',
      'refinery1',
      [],
      {
        description:
          'Dries natural gas to transmission dew-point specification',
        properties: {
          water_spec: '<2 ppmv',
          train_count: 3,
        },
      },
    );
    assets.push(dehydrationUnit);

    const compressorTrain = new Asset(
      'equip2',
      'Normandy Export Compressor Train C-830A/B',
      AssetType.EQUIPMENT,
      '',
      'refinery1',
      [],
      {
        description: 'Dual-train compression for national transmission export',
        properties: {
          rated_power: '2 x 22 MW',
          suction_pressure: '38 bar',
          discharge_pressure: '78 bar',
        },
      },
    );
    assets.push(compressorTrain);

    const gasExportPipeline = new Asset(
      'pipe5',
      'Normandy Gas Export Pipeline P-840',
      AssetType.PIPELINE,
      '',
      'refinery1',
      [],
      {
        description: 'Dry gas export pipeline to national transmission system',
        properties: {
          diameter: '36 inch',
          length: '12 km',
          max_flow: '20 mmscmd',
        },
      },
    );
    assets.push(gasExportPipeline);

    const custodyGasMeter = new Asset(
      'flow10',
      'Custody Gas Metering Skid FM-841',
      AssetType.FLOWMETER,
      '',
      'pipe5',
      [],
      {
        description: 'Ultrasonic custody transfer metering for dry gas export',
        properties: {
          range: '0-1200000 Sm3/hr',
          accuracy: '±0.2%',
          current_flow: '840000 Sm3/hr',
        },
      },
    );
    assets.push(custodyGasMeter);

    const gasBalanceProcess = new Asset(
      'process1',
      'Normandy Gas Balance and Allocation Process',
      AssetType.PROCESS,
      '',
      'refinery1',
      [],
      {
        description:
          'Hourly balancing workflow for nominations and allocations',
        properties: {
          schedule: 'hourly',
          criticality: 'high',
        },
      },
    );
    assets.push(gasBalanceProcess);

    const dryGasStream = new Asset(
      'stream1',
      'Dry Gas Export Stream',
      AssetType.STREAM,
      '',
      'process1',
      [],
      {
        description:
          'Commercial gas stream delivered to transmission customers',
        properties: {
          methane_content: '93.4%',
          gross_calorific_value: '39.2 MJ/Sm3',
        },
      },
    );
    assets.push(dryGasStream);

    return assets;
  }

  /**
   * Simulate network delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
